import { useMemo, type ReactNode } from 'react';
import { ExternalLink, RotateCw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import type { PicklistItemGroupDto, PostingBreakdownRow } from '../types';
import { ReceivingItemType, SerialDisposition, SERIAL_DISPOSITION_LABELS } from '../types';

/**
 * The fields the popover reads. The Items row passes its item group (PT-7 A5); `plannedLines` names each
 * posting line's MRS when the item has two or more.
 */
type BreakdownItem = Pick<
  PicklistItemGroupDto,
  'itemType' | 'itemCode' | 'itemName' | 'quantityPosted' | 'quantityPicked' | 'unitOfMeasure'
> & { plannedLines?: Pick<PicklistItemGroupDto['plannedLines'][number], 'id' | 'mrsNumber'>[] };

interface PostingBreakdownPopoverProps {
  item: BreakdownItem;
  /** Rows for THIS item (already filtered to its lines). undefined = not fetched yet. */
  rows: PostingBreakdownRow[] | undefined;
  loading: boolean;
  error: string | null;
  onOpen: () => void;
  onRetry: () => void;
  /** The clickable Progress-cell content (the trigger). */
  children: ReactNode;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * 86d3n1xum — posting-allocation breakdown. Renders what a picklist line actually issued to MIS,
 * per (bin, serial/lot), grouped by MIS batch. The issued identity comes from the issuance ledger
 * (not the picked slot); for serials it flags a swap (issued != picked) + its disposition.
 */
export function PostingBreakdownPopover({
  item,
  rows,
  loading,
  error,
  onOpen,
  onRetry,
  children,
}: PostingBreakdownPopoverProps) {
  const isSerial = item.itemType === ReceivingItemType.Serialized;

  // PT-7 A5 — with two or more planned lines, each row names its line's MRS. A row's `picklistItemId` is the
  // posting line, and a rebalance never moves a slot that has issuance rows, so the id → number is stable.
  const mrsByLine = useMemo(() => {
    const lines = item.plannedLines ?? [];
    return lines.length >= 2 ? new Map(lines.map((l) => [l.id, l.mrsNumber] as const)) : null;
  }, [item.plannedLines]);

  // Data-driven columns: show the serial/lot column only if any row carries one (hidden for pure
  // inventory); show the expiry column only if any row has a lot expiry.
  const showIdentity = useMemo(() => (rows ?? []).some((r) => r.issuedInventoryNumberId != null), [rows]);
  const showExpiry = useMemo(() => (rows ?? []).some((r) => r.expirationDate != null), [rows]);

  // Group by MIS batch (a line can post across several).
  const groups = useMemo(() => {
    const byMis = new Map<string, { misTranId: string | null; misId: string | null; misUrl: string; postedAt: string | null; postedByName: string | null; rows: PostingBreakdownRow[] }>();
    for (const r of rows ?? []) {
      const key = r.misId ?? r.misTranId ?? '—';
      const g = byMis.get(key);
      if (g) g.rows.push(r);
      else byMis.set(key, { misTranId: r.misTranId, misId: r.misId, misUrl: r.misUrl, postedAt: r.postedAt, postedByName: r.postedByName, rows: [r] });
    }
    return Array.from(byMis.values());
  }, [rows]);

  return (
    <Popover onOpenChange={(open) => { if (open) onOpen(); }}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-[440px] max-w-[92vw] max-h-[440px] overflow-auto p-0">
        <div className="px-3 py-2 border-b">
          <div className="font-mono text-xs font-medium">{item.itemCode}</div>
          <div className="text-xs text-muted-foreground truncate" title={item.itemName}>{item.itemName}</div>
          <div className="text-xs mt-0.5">
            <span className="font-semibold text-success">{item.quantityPosted}</span>
            {' of '}{item.quantityPicked} {item.unitOfMeasure} posted
          </div>
        </div>

        {loading ? (
          <div className="p-3 space-y-2">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-4/5" />
          </div>
        ) : error ? (
          <div className="p-3 flex items-center justify-between gap-2">
            <span className="text-xs text-destructive">{error}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={onRetry}>
              <RotateCw className="h-3 w-3 mr-1" /> Retry
            </Button>
          </div>
        ) : groups.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground text-center">No posting allocation recorded for this item.</div>
        ) : (
          <div className="divide-y">
            {groups.map((g, gi) => (
              <div key={g.misId ?? g.misTranId ?? gi} className="px-3 py-2">
                <div className="flex items-center justify-between gap-2 text-xs mb-1.5">
                  {g.misId ? (
                    <a
                      href={g.misUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-info hover:underline inline-flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {g.misTranId ?? `MIS #${g.misId}`}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-mono text-muted-foreground">{g.misTranId ?? '—'}</span>
                  )}
                  <span
                    className="text-muted-foreground truncate"
                    title={`${fmtDateTime(g.postedAt)}${g.postedByName ? ` · ${g.postedByName}` : ''}`}
                  >
                    {fmtDateTime(g.postedAt)}{g.postedByName ? ` · ${g.postedByName}` : ''}
                  </span>
                </div>

                <div className="space-y-1">
                  {g.rows.map((r, ri) => {
                    // 86d3nauca — swap = issued != ALLOCATED (a pick-time swap makes issued == picked, so the
                    // old issued-vs-picked basis missed it). Fall back to picked for legacy posted rows whose
                    // allocated is null (their adjustment log was written on the picked basis).
                    const swapBasis = r.allocatedInventoryNumberId ?? r.pickedInventoryNumberId;
                    const swapped = isSerial && r.issuedInventoryNumberId != null && swapBasis != null && r.issuedInventoryNumberId !== swapBasis;
                    const issued = r.issuedInventoryNumber ?? (r.issuedInventoryNumberId != null ? `#${r.issuedInventoryNumberId}` : '—');
                    return (
                      <div key={ri} className="flex items-center gap-2 text-xs">
                        <span className="font-mono w-24 shrink-0">{r.binCode}</span>
                        {mrsByLine && (
                          <span className="w-24 shrink-0 font-mono text-muted-foreground">
                            {mrsByLine.get(r.picklistItemId) ?? '—'}
                          </span>
                        )}
                        {showIdentity && (
                          <span className="flex-1 flex items-center gap-1.5 min-w-0">
                            <span className="font-mono truncate" title={issued}>
                              {issued}
                            </span>
                            {swapped && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 text-warning border-warning/40 shrink-0">
                                ⇄ swapped
                              </Badge>
                            )}
                            {r.disposition != null && (
                              <Badge
                                variant="outline"
                                // PT-6 (17) — destructive only for a physical loss (Damaged, Missing), the activity
                                // severity rule; Returned to bin and a Swap serial's Taken elsewhere are muted.
                                className={`text-[9px] px-1 py-0 shrink-0 ${
                                  r.disposition === SerialDisposition.Damaged || r.disposition === SerialDisposition.Missing
                                    ? 'text-destructive border-destructive/40'
                                    : 'text-muted-foreground'
                                }`}
                              >
                                {SERIAL_DISPOSITION_LABELS[r.disposition]}
                              </Badge>
                            )}
                          </span>
                        )}
                        <span className="w-12 text-right shrink-0 tabular-nums">{r.quantity}</span>
                        {showExpiry && (
                          <span className="w-24 text-right text-muted-foreground shrink-0">
                            {r.expirationDate ? fmtDate(r.expirationDate) : '—'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
